import React, { useState, useRef, useEffect, useContext } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/high-res.css'
import './styles.scss'
import { Context } from '../../contexts/GlobalContext'
import Spinner from '../spinner'
import Axios from '../../utilities/axios'
import ToolTip from '../tool-tip'

const modeSpecificPanelMessages = {
  'email': {
    title: 'Email Verification',
    sub1: 'Verify your email address by emailing a code in your email. Click Send Code to receive verification code in your inbox.',
    boxTitle: 'Your email address:',
    boxButton: 'Email Code',
    titleIcon: 'fal fa-envelope-open-text',
    widgetIcon: 'fal fa-mail-bulk',
    sendCodeEndpoint: '/verify/email/initiate',
    verifyCodeEndpoint: '/verify/email/match',
    resendWait: 120000        // 2 * 60 * 1000
  },
  'phone': {
    title: 'Phone Verification',
    sub1: 'Verify your phone number by texting code to your phone. Click Send Code to receive verification code in your text inbox.',
    boxTitle: 'Enter phone number:',
    boxButton: 'Send Code',
    titleIcon: 'fal fa-mobile',
    widgetIcon: 'fal fa-comment-alt-lines',
    sendCodeEndpoint: '/verify/phone/initiate',
    verifyCodeEndpoint: '/verify/phone/match',
    resendWait: 300000      // 5 * 60 * 1000
  }
}

const tokenInitiationErrors = {
  'email-not-found': 'No email address found',
  'email-already-verified': 'Email already verified',
  'email-too-many-token-requests': 'Too many requests. Retry after 2 minutes',
  'email-failed-send': 'Email sending failed. Retry!',
  'phone-already-verified': 'Phone already verified',
  'phone-already-exists': 'Phone used by another account',
  'phone-too-many-token-requests': 'Too many requests. Retry after 5 minutes',
  'phone-invalid-format': 'Invalid phone number format',
  'phone-failed-send': 'Failed to send text. Retry!',
  'too-many-failed-attempts': 'Too many attempts, try tomorrow',
  'server-error': 'Server error occurred',
  'unknown': 'Failed to Send Code'
}

const useTokenExpiryHandler = (tokenExpiry, clearTokenState, setShowVerification) => {
  useEffect(() => {
    if (!tokenExpiry) return

    const expiryDate = new Date(tokenExpiry)
    if (isNaN(expiryDate.getTime())) return

    const delay = expiryDate - new Date()
    if (delay <= 0) {
      clearTokenState()
      setShowVerification(false)
      return
    } else {
      setShowVerification(true)
    }

    const timeoutId = setTimeout(() => {
      clearTokenState()
      setShowVerification(false)
    }, delay)

    return () => clearTimeout(timeoutId)
  }, [tokenExpiry, clearTokenState])
}

const VerificationPanel = ({ mode='email', onCompletion=() => {}, verification = {} }) => {
  const {globalState} = useContext(Context)
  const { user = {} } = globalState

  const [values, setValues] = useState(['', '', '', '', '', ''])
  const [target, setTarget] = useState( mode === 'phone' ? user?.phone || '' : user?.email || 'No Email Found')
  
  const [isLoadingSendCode, setIsLoadingSendCode] = useState(false)
  const [isLoadingVerification, setIsLoadingVerification] = useState(false)

  const [verificationState, setVerificationState] = useState(verification[mode]?.isVerified === true ? 'success' : 'not-ready')   // 'not-ready', 'ready', 'incorrect', 'failed', 'success'
  const [tokenExpiry, setTokenExpiry] = useState(verification[mode]?.tokenExpiry || null)
  const [tokenExpiryError, setTokenExpiryError] = useState('')
  
  const inputRefs = useRef([])
  const sendCodeInputRef = useRef(null)

  const [showVerification, setShowVerification] = useState(false)

  const panelMessages = modeSpecificPanelMessages[mode]

  useEffect(() => {
    // Initialize refs array
    inputRefs.current = inputRefs.current.slice(0, 6)
    
    if (!tokenExpiry && !target) {
      togglePhoneInputFocus(true)
      // sendCodeInputRef.current.focus()
    } else if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [])

  useEffect(() => {
    // Auto blur when all inputs are filled
    const isComplete = values.every(value => value !== '')
    if (isComplete) {
      if(verificationState !== 'success') {
        setVerificationState('ready')
      }
      setTimeout(() => {
        document.activeElement?.blur()
      }, 45)
    } else {
      if(verificationState !== 'success') {
        setVerificationState('not-ready')
      }
    }
  }, [values])

  useTokenExpiryHandler( tokenExpiry, () => { setTokenExpiry(null) }, setShowVerification, mode )

  if(!['phone', 'email'].includes(mode)) {
    return null
  }

  const togglePhoneInputFocus = (focus = true) => {
    const input = document.querySelector('.react-tel-input .form-control')
    if (input) {
      if (focus) {
        input.focus()
      } else {
        input.blur()
      }
    }
  }

  const handleChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1)
    }

    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      return
    }

    const newValues = [...values]
    newValues[index] = value
    setValues(newValues)

    // Move to next input if current is filled
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = inputRefs.current[index - 1]
      if (prevInput) {
        prevInput.focus()
        // Set cursor position to the end of the input
        setTimeout(() => {
          prevInput.setSelectionRange(prevInput.value.length, prevInput.value.length)
        }, 0)
      }
    }
    if (e.key === 'ArrowRight' && index < 5) {
      const nextInput = inputRefs.current[index + 1]
      if (nextInput) {
        nextInput.focus()
        // Set cursor position to the end of the input
        setTimeout(() => {
          nextInput.setSelectionRange(nextInput.value.length, nextInput.value.length)
        }, 0)
      }
    }
  }

  const handlePaste = (index, e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')  // Remove non-digits
    
    if (pastedData) {
      const newValues = [...values]
      
      // Fill from current index onwards
      for (let i = 0; i < pastedData.length && (index + i) < 6; i++) {
        newValues[index + i] = pastedData[i]
      }
      
      setValues(newValues)
      
      // Focus the next empty input or the last filled input
      const nextFocusIndex = Math.min(index + pastedData.length, 5)
      inputRefs.current[nextFocusIndex]?.focus()
    }
  }

  const handleVerification = async () => {
    if(verificationState === 'success' || isLoadingVerification) {
      return null
    }
    setIsLoadingVerification(true)
    const code = values.join('')
    try {
      const { success } = (await Axios.post(panelMessages?.verifyCodeEndpoint, { token: code })).data
      if(success === true) {
        setVerificationState('success')
      } else {
        setVerificationState('failed')
      }
    } catch (error) {
      const errorCode = error?.response?.data?.error === 'expired' ? 'expired' : 'failed'
      setVerificationState(errorCode)
    } finally {
      setIsLoadingVerification(false)
    }
  }

  const handleClear = () => {
    setValues(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus()
  }

  const sendCodeHandler = async () => {
    if(isLoadingSendCode === true || !target || verificationState === 'success') {
      return null
    }
    setIsLoadingSendCode(true)
    try {
      const endPoint = panelMessages?.sendCodeEndpoint || ''
      const payload = { ...( mode === 'phone' ? { phone: target } : {} ) }
      const { success, message, error = 'unknown', data = {} } = (await Axios.post(endPoint, payload))?.data
      if(success === true) {
        setTokenExpiry(data?.tokenExpiry)
        setTokenExpiryError('')
        setShowVerification(true)
      } else {
        setTokenExpiryError(tokenInitiationErrors[error] || tokenInitiationErrors['unknown'])
        setTokenExpiry(null)
        setShowVerification(false)
      }
    } catch (error) {
      const _error = error?.response?.data?.error || 'unknown'
      setTokenExpiry(null)
      setTokenExpiryError(tokenInitiationErrors[_error])
      setShowVerification(false)
    } finally {
      setIsLoadingSendCode(false)
    }
  }

  let verificationStateLabel = null
  // Five states: 'not-ready', 'ready', 'incorrect', 'failed', 'success'
  switch (verificationState) {
    case 'success':
      verificationStateLabel = (
        <div className='sv2-signin-verification-button'>
          <i className="fas fa-check-circle" style={{color: 'rgb(29, 191, 113)'}}></i>
          <span style={{marginLeft: '5px'}}>Code Verified</span>
        </div>
      )
      break
    case 'expired':
      verificationStateLabel = (
        <div className='sv2-signin-verification-button'>
          <i className="fas fa-exclamation-circle" style={{color: 'orange'}}></i>
          <span style={{marginLeft: '5px'}}>Incorrect Code or Expired</span>
        </div>
      )
      break
    case 'failed':
      verificationStateLabel = (
        <div className='sv2-signin-verification-button'>
          <i className="fas fa-exclamation-circle" style={{color: 'orange'}}></i>
          <span style={{marginLeft: '5px'}}>Verification Failed</span>
        </div>
      )
      break
    case 'ready':
      verificationStateLabel = (
        <div className={`sv2-signin-verification-button-main`} onClick={handleVerification}>
          Verify Code
        </div>
      )
      break
    default:
      verificationStateLabel = (
        <div className={`sv2-signin-verification-button-main disabled`}>
          Verify Code
        </div>
      )
      break
  }

  let displayTokenExpiry = null 
  let sendCodeButton = null
  if (tokenExpiry) {
    const expirayDateTime = new Date(tokenExpiry)
    const resendDateTime = new Date(tokenExpiry)

    const adjustedDelay = mode === 'phone' ? 5 : 28
    resendDateTime.setMinutes(resendDateTime.getMinutes() - adjustedDelay)

    displayTokenExpiry = expirayDateTime > Date.now() ? (
      <span style={ {fontSize: '10px'} }><i className='fal fa-check' style={{color: 'rgb(29, 191, 113)'}}></i> Code Sent Successfully</span>
    ) : null

    sendCodeButton = resendDateTime > Date.now() ?
      <ToolTip 
        view={<div><div style={{marginBottom: '4px', fontWeight: 600}}>Haven’t received the code yet?</div>{`You can try resending it after ${resendDateTime.toLocaleTimeString()}`}</div>}
        cursor='not-allowed'
        styles={ {
          color: 'rgb(230, 230, 230)', backgroundColor: '#1d1f24', lineHeight: 1.4, width: '150px',
          padding: '7.5px 10px', marginBottom: '5px', border: '1px solid rgba(255, 255, 255, 0.1)'
        } }
      >
        <div className='sv2-signin-send-button'>
          { panelMessages.boxButton }
        </div>
      </ToolTip>
      :
      <div 
        className='sv2-signin-send-button' 
        onClick={sendCodeHandler}
        style={ {...(!target ? {cursor: 'not-allowed', color: 'rgb(230, 230, 230)'} : {})} }
      >
        { panelMessages.boxButton }
      </div>
  } else {
    sendCodeButton = (
      <div 
        className='sv2-signin-send-button' 
        onClick={sendCodeHandler}
        style={ {...(!target ? {cursor: 'not-allowed', color: 'rgb(230, 230, 230)'} : {})} }
      >
        { panelMessages.boxButton }
      </div>
    )
    if(tokenExpiryError) {
      displayTokenExpiry = (
        <span style={ {fontSize: '10px'} }><i className='fal fa-exclamation-circle' style={{color: 'orange'}}></i> {tokenExpiryError}</span>
      )
    }
  }
  if (isLoadingSendCode) {
    displayTokenExpiry = <Spinner dark={true}/>
  }

  const isInputDisabled = (() => {
    if (verificationState === 'success') return true
    if (mode === 'email') return true
    if (tokenExpiry) return true
    return false
  })()

  const targetInputBox = mode === 'phone' ? (
    <div style={ {position: 'relative'} }>
      { !!!(target) && <i className="fal fa-globe" style={ { color: 'rgba(255,255, 255, 0.7)', fontSize: '16px', position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '18px' } }></i> }
      <PhoneInput
        containerClass={!!!(target) ? 'custom-phone-ip-container' : ''}
        inputStyle={ { 
          borderRadius: '7.5px',
          backgroundColor: 'transparent',
          color: 'rgba(230, 230, 230, 0.9)',
          border: '1px solid rgb(70, 70, 70)',
          height: '40px'
        } }
        country=''
        containerStyle={{ marginBottom: '10px' }}
        jumpCursorToEnd={true}
        disableDropdown={true}
        value={target}
        onChange={ phone => { setTarget(phone) } }
        disabled={isInputDisabled}
      />
    </div>
  ) : (
    <input 
      className='sv2-sigin-target-input'
      ref={sendCodeInputRef}
      style={{marginBottom: '10px'}}
      value={target}
      disabled={isInputDisabled}
      onChange={ e => { setTarget(e.target.value) } }
    />
  )

  return (
    <div className='sv2-signin-container'>
      <div className='sv2-signin-title'>Verify Account</div>
      <div className='sv2-signin-sub-title'
        style={{display: 'flex', alignItems: 'center'}}
      >
        <i style={{ fontSize: '22px', opacity: 0.5 }} className={panelMessages.titleIcon}></i>
        &nbsp;&nbsp;&nbsp;{ panelMessages.title }&nbsp;&nbsp;{ (verificationState === 'success') && <i className="fas fa-check-circle" style={{color: 'rgb(29, 191, 113)'}}></i> }
      </div>
      <p className='sv2-sigin-sub-desc'>
        { panelMessages.sub1 }
      </p>
      <div className='sv2-signin-widget'>
        <div className='sv2-signin-widget-icon'>
          <i className={panelMessages.widgetIcon}></i>
        </div>
        <div className='sv2-signin-widget-body'>
          <div className='sv2-signin-sub-title' style={{ marginBottom: '12.5px'}}>
            {panelMessages.boxTitle}
          </div>
          {targetInputBox}
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            {sendCodeButton}
            {displayTokenExpiry}
          </div>
        </div>
      </div>
      <div className={`sv2-signin-animated-container${showVerification ? ' show' : ''}`} >
        <div className='sv2-signin-sub-title' style={{ display: 'flex', alignItems: 'center'}}>
          <AsteriskIcon/>&nbsp;&nbsp;&nbsp;Enter Verification Code
        </div>
        <p className='sv2-sigin-sub-desc' style={{ position: 'relative' }}>
          Enter the six digit code below
          {
            values.some(v => Boolean(v)) &&
            <span className='sv2-sigin-code-clear' onClick={handleClear}>
              Clear
            </span>
          }
        </p>
        <div className='sv2-signin-numbers-container'>
          {values.map((value, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              value={value}
              disabled={verificationState === 'success'}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={e => handlePaste(index, e)}
              className='sv2-sigin-numbers-input'
              maxLength={1}
              onFocus={(e) => {
                const value = e.target.value
                e.target.setSelectionRange(value.length, value.length)
              }}
            />
          ))}
        </div>
        <div style={{width: '100%', margin: '25px 0 50px 0', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          {verificationStateLabel}
        </div>
        <div 
          className={verificationState === 'success' ? `sv2-signin-status` : `sv2-signin-status disable`} 
          onClick={verificationState === 'success' ? onCompletion : () => {} }
        >
          Next
        </div>
      </div>
    </div>
  )
}

const AsteriskIcon = ({ size = 7 }) => {
  return (
    <div
      style={{
        display: 'inline-block',
        border: '1px solid rgb(230, 230, 230)',
        fontWeight: 600,
        borderRadius: '3px',
        padding: `0.5px 1.5px 0.5px 2.0px`,
        fontFamily: 'monospace',
        fontSize: `${size}px`,
        lineHeight: 1,
        letterSpacing: '0.5px',
        verticalAlign: 'baseline',
        opacity: 0.5
      }}
    >
      ****
    </div>
  )
}

const AccountSetupPanel = () => {
  const { globalState, setGlobalState } = useContext(Context)
  const { user = {} } = globalState

  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const [allSteps, setAllSteps] = useState(['int-step-0', 'exit'])
  const [currentStep, setCurrentStep] = useState(allSteps[0])
  const [verification, setVerification] = useState({})

  const checkAccountStatus = async () => {
    if (isCheckingStatus) return

    setIsCheckingStatus(true)
    try {
      const { success, data } = (await Axios.get('/verify/status')).data
      if (!success) {
        throw 'Account status check failed!'
      }
      const isEmailVerified = data.email?.isVerified || false
      const isPhoneVerified = data.phone?.isVerified || false
      const modifiedAllSteps = [
        ...(isEmailVerified ? [] : ['email', 'int-step-1']), 
        ...(isPhoneVerified ? [] : ['phone', 'int-step-2']), 
        'exit'
      ]
      if (user?.accountStatus !== data?.accountStatus) {
        setGlobalState(prev => ({
          ...prev,
          user: { ...prev.user, accountStatus: data?.accountStatus }
        }))
      }
      setVerification({
        phone: data?.phone || {},
        email: data?.email || {}
      })
      setAllSteps(modifiedAllSteps)
      setCurrentStep(modifiedAllSteps[0])
    } catch (error) {
      setAllSteps(['int-step-0'])
      setCurrentStep('int-step-0')
    } finally {
      setIsCheckingStatus(false)
    }
  }

  const gotoNextStep = () => {
    const nextIndex = allSteps.findIndex(v => v === currentStep) + 1
    const nextStep = allSteps[Math.min(nextIndex, allSteps?.length - 1)] || 'exit'
    setCurrentStep(nextStep)
  }

  useEffect(() => {
    if (user?.accountStatus === 'pending_verification') {
      checkAccountStatus()
    }
  }, [])

  let view = null

  if (isCheckingStatus) {
    view = <IntermediarySetupPanel mode='int-step-0'/>
  } else if (currentStep === 'exit') {
    return null
  } else if (['int-step-1', 'int-step-2', 'exit'].includes(currentStep)) {
    view = (<IntermediarySetupPanel mode={currentStep} onCompletion={gotoNextStep}/>)
  } else if (['email', 'phone'].includes(currentStep)) {
    view = (<VerificationPanel mode={currentStep} onCompletion={gotoNextStep} verification={verification}/>)
  }

  if (!view) {
    return null
  }

  return (
    <div className='sv2-signin-main'>
      { view }
    </div>
  )
}

const IntermediarySetupPanel = ({ mode = 'int-step-1', onCompletion=() => {} }) => {
  const commonStyle = {
    marginLeft: '-20.5px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    gap: '7.5px',
  }
  let steps = []
  let infoText = `You can ask Nelima to schedule tasks for the future, even on a recurring basis.`

  if (mode === 'int-step-0') {
    steps = [
      { done: false, text: 'Checking account status' },
      { done: false, text: 'Check for verification', spinnerStyle: { borderTopColor: '#323741' } },
      { done: false, text: 'Final setup', spinnerStyle: { borderTopColor: '#323741' } },
    ]
  } else {
    steps = [
      { done: true, text: 'Email Verification Complete' },
      {
        done: mode !== 'int-step-1',
        text:
          mode === 'int-step-1'
            ? 'Phone Verification in process'
            : 'Phone Verification Complete',
      },
      { done: false, text: 'Finalizing Setup', spinnerStyle: mode === 'int-step-1' ? { borderTopColor: '#323741' } : {} },
    ]
    infoText = mode === 'int-step-1' ? 
      `You can ask Nelima to look for files and documents saved in the storage and modify them.`
      :
      `You can ask Nelima to search for information across websites, document files, and images.`
  }
  
  useEffect(() => {
    if (mode === 'int-step-0') {
      return
    }
    const randLoadingTime = Math.floor(Math.random() * (3500 - 2000 + 1)) + 2000
    const timeoutId = setTimeout(() => {
      onCompletion()
    }, randLoadingTime)

    return () => clearTimeout(timeoutId)
  }, [])

  return (
    <div className="sv2-signin-container">
      <div className="sv2-signin-widget" style={{ marginTop: 0, alignItems: 'center' }}>
        <div className="sv2-signin-widget-icon">
          <i className="fal fa-clipboard-list-check"></i>
        </div>
        <div className="sv2-signin-widget-body" style={{ marginLeft: '30px' }}>
          <div className="sv2-signin-sub-title" style={{ margin: '5px 0 12.5px 0' }}>
            Setting Up Account
          </div>

          {steps.map((step, i) => (
            <div key={i} style={commonStyle}>
              {step.done ? (
                <i style={{ color: 'rgb(29, 191, 113)' }} className="fas fa-check-circle"></i>
              ) : (
                <Spinner size={14} dark={true} style={step.spinnerStyle} />
              )}
              <span style={{ opacity: 0.5, fontWeight: 500, fontStyle: 'italic' }}>{step.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div
        className="sv2-signin-sub-title"
        style={{ display: 'flex', alignItems: 'center', gap: '12.5px', marginTop: '22.5px' }}
      >
        <i className="fal fa-lightbulb" style={{ opacity: 0.5 }}></i>Did you know?
      </div>
      <p className="sv2-sigin-sub-desc">
        {infoText}
      </p>
    </div>
  )
}


export default AccountSetupPanel